import { defineStore } from "pinia";
import { watch as watchLive } from "@/components/toLoad/live";
import { apiUser } from "@/components/toLoad/api";
import { setSnackbar } from "@/stores/ui";

const OPEN_STATUSES = new Set([1, 2, "1", "2"]);
const HYDRATE_CHUNK = 10;
const SUPPRESS_MS = 2000;

function currentPath() {
  try {
    const router = require("@/router").default;
    return router.currentRoute?.path || "";
  } catch {
    return "";
  }
}

function dueMs(dueDate) {
  if (!dueDate) return Infinity;
  if (typeof dueDate.toDate === "function") return dueDate.toDate().getTime();
  if (dueDate._seconds != null) return dueDate._seconds * 1000;
  if (dueDate.seconds != null) return dueDate.seconds * 1000;
  const parsed = new Date(dueDate).getTime();
  return Number.isNaN(parsed) ? Infinity : parsed;
}

function taskTitle(task) {
  return task?.task || "Sans titre";
}

function authorKey(task) {
  if (!task) return null;
  if (typeof task.author === "string") return task.author;
  if (task.authorId) return task.authorId;
  if (task.author?.id) return task.author.id;
  return null;
}

function mergeTask(prev, raw) {
  const authorId = typeof raw.author === "string" ? raw.author : (raw.authorId || authorKey(raw));
  const next = {
    ...raw,
    id: raw.id,
    authorId,
  };

  if (typeof raw.author === "object" && raw.author)
    next.author = raw.author;
  else if (prev?.author && typeof prev.author === "object")
    next.author = prev.author;
  else
    next.author = raw.author;


  if (prev?.participantsData && Array.isArray(prev.participantsData)) {
    const sameParticipants =
      JSON.stringify(prev.participants || []) === JSON.stringify(raw.participants || []);
    if (sameParticipants) next.participantsData = prev.participantsData;
  }

  if (raw.projectData?.title)
    next.projectData = raw.projectData;
  else if (prev?.projectData)
    next.projectData = prev.projectData;


  return next;
}

function needsHydration(task, hydratedIds) {
  if (!task?.id) return false;
  if (!hydratedIds[task.id]) return true;
  if (authorKey(task) && typeof task.author !== "object") return true;
  if ((task.participants || []).length && !task.participantsData) return true;
  return false;
}

export const useTasksStore = defineStore("tasks", {
  state: () => ({
    byId: {},
    ids: [],
    companyId: null,
    loadStatus: "idle",
    partialReady: false,
    peopleById: {},
    hydratedIds: {},
    unsub: null,
    primed: false,
    suppressUntil: {},
    suppressAddedUntil: 0,
    hydrating: false,
  }),

  getters: {
    list(state) {
      return state.ids.map((id) => state.byId[id]).filter(Boolean);
    },

    openPartial(state) {
      return state.ids
        .map((id) => state.byId[id])
        .filter((task) => task && OPEN_STATUSES.has(task.status))
        .sort((a, b) => dueMs(a.dueDate) - dueMs(b.dueDate))
        .slice(0, 10);
    },

    forProject: (state) => (projectId) => {
      if (!projectId) return [];
      return state.ids
        .map((id) => state.byId[id])
        .filter((task) => task && task.project === projectId);
    },

    showSkeleton(state) {
      return (
        (state.loadStatus === "loading" || state.loadStatus === "idle") &&
        !state.partialReady &&
        state.ids.length === 0
      );
    },
  },

  actions: {
    resetState() {
      if (this.unsub) {
        this.unsub();
        this.unsub = null;
      }
      this.byId = {};
      this.ids = [];
      this.companyId = null;
      this.loadStatus = "idle";
      this.partialReady = false;
      this.peopleById = {};
      this.hydratedIds = {};
      this.primed = false;
      this.suppressUntil = {};
      this.suppressAddedUntil = 0;
      this.hydrating = false;
    },

    suppressLocal(id) {
      if (!id) return;
      this.suppressUntil = {
        ...this.suppressUntil,
        [id]: Date.now() + SUPPRESS_MS,
      };
    },

    suppressAdds() {
      this.suppressAddedUntil = Date.now() + SUPPRESS_MS;
    },

    isSuppressed(id) {
      const until = this.suppressUntil[id];
      return until != null && Date.now() < until;
    },

    ensureWatch(companyId) {
      if (!companyId) {
        this.resetState();
        return;
      }

      if (this.companyId === companyId && this.unsub) return;

      if (this.unsub) {
        this.unsub();
        this.unsub = null;
      }

      if (this.companyId !== companyId) {
        this.byId = {};
        this.ids = [];
        this.peopleById = {};
        this.hydratedIds = {};
        this.partialReady = false;
        this.primed = false;
        this.suppressUntil = {};
        this.suppressAddedUntil = 0;
      }

      this.companyId = companyId;
      this.loadStatus = this.ids.length ? "refreshing" : "loading";

      const scopedCompanyId = companyId;
      this.unsub = watchLive(scopedCompanyId, "tasks", (res, meta = {}) => {
        if (this.companyId !== scopedCompanyId) return;
        const docs = (res.docs || []).map((document) => ({
          ...document.data(),
          id: document.id,
        }));
        this.applyDocs(docs, scopedCompanyId, meta);
      });
    },

    applyDocs(docs, companyId, meta = {}) {
      if (companyId !== this.companyId) return;

      const isSnapshot = meta.type !== "change";
      const prevById = this.byId;
      const nextById = {};
      const nextIds = [];

      for (const raw of docs) {
        nextById[raw.id] = mergeTask(prevById[raw.id], raw);
        nextIds.push(raw.id);
      }

      if (!isSnapshot && this.primed && meta.type === "change")
        this.notifyChange(meta, prevById, nextById);


      this.byId = nextById;
      this.ids = nextIds;
      this.primed = true;
      this.loadStatus = "ready";

      const nextHydrated = { ...this.hydratedIds };
      for (const id of Object.keys(nextHydrated))
        if (!nextById[id]) delete nextHydrated[id];

      for (const id of nextIds) {
        const prev = prevById[id];
        const next = nextById[id];
        if (
          prev &&
          (JSON.stringify(prev.participants || []) !==
            JSON.stringify(next.participants || []) ||
            authorKey(prev) !== authorKey(next))
        )
          delete nextHydrated[id];
      }
      this.hydratedIds = nextHydrated;

      this.hydrateMissing();
    },

    notifyChange(meta, prevById, nextById) {
      const id = meta.id;
      if (!id || this.isSuppressed(id)) return;

      const onTasksPage = currentPath() === "/tasks";
      const op = meta.op;

      if (op === "added") {
        if (Date.now() < this.suppressAddedUntil) return;
        const task = nextById[id] || meta.data;
        const text = onTasksPage
          ? `Tâche ajoutée : ${taskTitle(task)}`
          : `Nouvelle tâche : ${taskTitle(task)}`;
        setSnackbar({ text });
        return;
      }

      if (!onTasksPage) return;

      if (op === "modified") {
        setSnackbar({
          text: `Tâche modifiée : ${taskTitle(nextById[id] || meta.data)}`,
        });
        return;
      }

      if (op === "removed")
        setSnackbar({
          text: `Tâche supprimée : ${taskTitle(prevById[id])}`,
        });
    },

    async fetchPartial(companyId) {
      if (!companyId) return;
      if (this.companyId && this.companyId !== companyId) return;
      if (!this.companyId) this.companyId = companyId;

      try {
        const { data } = await apiUser({
          method: "get",
          url: `/companies/${companyId}/tasks/partial`,
          params: { statuses: "1,2", limit: 10 },
        });
        if (this.companyId !== companyId) return;

        const docs = data.documents || [];
        this.partialReady = true;

        if (this.primed) return;

        const nextById = { ...this.byId };
        const nextIds = [...this.ids];
        for (const raw of docs) {
          if (!raw?.id) continue;
          nextById[raw.id] = mergeTask(nextById[raw.id], raw);
          if (!nextIds.includes(raw.id)) nextIds.push(raw.id);
        }
        this.byId = nextById;
        this.ids = nextIds;
        this.hydrateMissing(docs.map((doc) => doc.id));
      } catch (error) {
        console.error("Error fetching tasks partial:", error);
      }
    },

    patchLocal(id, patch) {
      if (!id || !this.byId[id]) return;
      if (this.companyId == null) return;
      this.suppressLocal(id);
      this.byId = {
        ...this.byId,
        [id]: {
          ...this.byId[id],
          ...patch,
        },
      };
    },

    removeLocal(id) {
      if (!id || !this.byId[id]) return;
      this.suppressLocal(id);
      const rest = { ...this.byId };
      delete rest[id];
      this.byId = rest;
      this.ids = this.ids.filter((taskId) => taskId !== id);
      if (this.hydratedIds[id]) {
        const nextHydrated = { ...this.hydratedIds };
        delete nextHydrated[id];
        this.hydratedIds = nextHydrated;
      }
    },

    upsertLocal(task) {
      if (!task?.id) return;
      this.suppressLocal(task.id);
      const merged = mergeTask(this.byId[task.id], task);
      this.byId = {
        ...this.byId,
        [task.id]: merged,
      };
      if (!this.ids.includes(task.id)) this.ids = [...this.ids, task.id];
    },

    setProjectData(projectId, projectData) {
      if (!projectId) return;
      const nextById = { ...this.byId };
      let changed = false;
      for (const id of this.ids) {
        const task = nextById[id];
        if (!task || task.project !== projectId) continue;
        nextById[id] = {
          ...task,
          projectData,
        };
        changed = true;
      }
      if (changed) this.byId = nextById;
    },

    remapProjects(projects) {
      if (!projects?.length) return;
      const byProjectId = {};
      for (const project of projects)
        byProjectId[project.id] = {
          id: project.id,
          title: project.name || project.title,
          avatar: project.avatar,
        };

      const nextById = { ...this.byId };
      let changed = false;
      for (const id of this.ids) {
        const task = nextById[id];
        if (!task?.project) continue;
        const projectData = byProjectId[task.project];
        if (!projectData) continue;
        if (
          task.projectData?.title === projectData.title &&
          task.projectData?.avatar === projectData.avatar
        )
          continue;

        nextById[id] = {
          ...task,
          projectData,
        };
        changed = true;
      }
      if (changed) this.byId = nextById;
    },

    seedPeople(list) {
      if (!Array.isArray(list) || !list.length) return;
      const next = { ...this.peopleById };
      let changed = false;
      for (const person of list) {
        if (!person?.id || person.header != null) continue;
        if (next[person.id]) continue;
        next[person.id] = person;
        changed = true;
      }
      if (changed) this.peopleById = next;
    },

    async resolvePerson(personId) {
      if (!personId) return null;
      if (this.peopleById[personId]) return this.peopleById[personId];

      const companyId = this.companyId;
      if (!companyId) return null;

      try {
        const { data } = await apiUser({ url: `/users/${personId}` });
        const person = {
          avatar: data.avatar,
          civility: data.civility,
          firstName: data.firstName,
          lastName: data.lastName,
          computeName: data.computeName,
          email: data.email,
          company: data.company,
          id: data.id,
          report: false,
        };
        this.peopleById = {
          ...this.peopleById,
          [personId]: person,
        };
        return person;
      } catch {
        /* try customer */
      }

      try {
        const customer = await apiUser({
          method: "get",
          url: `/companies/${companyId}/customers/${personId}`,
        });
        this.peopleById = {
          ...this.peopleById,
          [personId]: customer.data,
        };
        return customer.data;
      } catch {
        /* try company */
      }

      try {
        const company = await apiUser({
          method: "get",
          url: `/companies/${companyId}/companies/${personId}`,
        });
        this.peopleById = {
          ...this.peopleById,
          [personId]: company.data,
        };
        return company.data;
      } catch {
        const fallback = {
          avatar: "",
          civility: "",
          firstName: "Inconnu",
          lastName: "",
          computeName: "",
          email: "",
          id: personId,
        };
        this.peopleById = {
          ...this.peopleById,
          [personId]: fallback,
        };
        return fallback;
      }
    },

    async hydrateOne(id) {
      const task = this.byId[id];
      if (!task || this.companyId == null) return;

      const authorId = authorKey(task);
      let author = task.author;
      if (authorId) {
        const resolved = await this.resolvePerson(authorId);
        if (resolved) author = resolved;
      }

      let participantsData = task.participantsData;
      if ((task.participants || []).length)
        participantsData = (
          await Promise.all(
            (task.participants || []).map((participantId) =>
              this.resolvePerson(participantId),
            ),
          )
        ).filter(Boolean);


      if (this.companyId == null || !this.byId[id]) return;

      this.byId = {
        ...this.byId,
        [id]: {
          ...this.byId[id],
          author,
          authorId,
          participantsData,
        },
      };
      this.hydratedIds = {
        ...this.hydratedIds,
        [id]: true,
      };
    },

    async hydrateMissing(onlyIds) {
      if (this.hydrating || !this.companyId) return;

      const pending = (onlyIds || this.ids).filter((id) =>
        needsHydration(this.byId[id], this.hydratedIds),
      );
      if (!pending.length) return;

      this.hydrating = true;
      try {
        for (let index = 0; index < pending.length; index += HYDRATE_CHUNK) {
          if (!this.companyId) break;
          const chunk = pending.slice(index, index + HYDRATE_CHUNK);
          await Promise.all(chunk.map((id) => this.hydrateOne(id)));
        }
      } finally {
        this.hydrating = false;
      }
    },
  },
});
